import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { SvgIcon } from '../../../../components';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../util/routes';
import { createSlug } from '../../../../util/urlHelpers';
import { formatLocationShort } from '../../../../util/stateNames';
import calendarIcon from '../../../../assets/calendar.png';
import locationIcon from '../../../../assets/location.png';
import css from './BidCard.module.css';

const BidCard = ({ bid, theme = 'light', makeOfferButtonClassName = null, onMakeOffer = null }) => {
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();

  const formatCityStateLocation = location => {
    if (!location) return 'Location TBD';

    const text = String(location).trim();
    const pipeParts = text
      .split('|')
      .map(part => part.trim())
      .filter(Boolean);
    const cityStateText = pipeParts.length > 1 ? pipeParts[pipeParts.length - 1] : text;
    return formatLocationShort(cityStateText) || 'Location TBD';
  };

  // Format date to "FRI, NOV 12 · 10:00 PM"
  const formatDate = (dateString, timeString) => {
    if (!dateString) return '';
    try {
      const dateText = String(dateString).trim();
      const dateOnlyMatch = dateText.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const date = dateOnlyMatch
        ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
        : new Date(dateText);

      if (isNaN(date.getTime())) return dateString;

      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const weekday = weekdays[date.getDay()];
      const month = months[date.getMonth()].toUpperCase();
      const day = date.getDate();
      const hasEmbeddedTime = /T|\d{1,2}:\d{2}/.test(dateText);
      const timeSource = timeString || (hasEmbeddedTime ? date : null);

      if (!timeSource) {
        return `${weekday.toUpperCase()}, ${month} ${day}`;
      }

      let hours;
      let minutes;
      const timeMatch =
        typeof timeSource === 'string' ? timeSource.match(/(\d{1,2}):(\d{2})/) : null;

      if (timeMatch) {
        hours = Number(timeMatch[1]);
        minutes = Number(timeMatch[2]);
      } else {
        hours = date.getHours();
        minutes = date.getMinutes();
      }

      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = String(minutes).padStart(2, '0');

      return `${weekday.toUpperCase()}, ${month} ${day} · ${displayHours}:${displayMinutes} ${ampm}`;
    } catch (e) {
      return dateString;
    }
  };

  // Navigate to Sharetribe's ListingPage (no-code UI flow)
  const navigateToListingPage = () => {
    // Use the original listing object if available, otherwise construct from bid data
    const listing = bid.listing;

    let listingId, listingTitle;
    if (listing) {
      // Use the actual listing object - this is the Sharetribe listing
      listingId = listing.id?.uuid || listing.id?.id?.uuid || listing.id || bid.id;
      listingTitle = listing.attributes?.title || bid.title || 'Bid';
    } else {
      // Fallback: construct from bid data (shouldn't happen if transformListingToBid is correct)
      listingId = bid.id;
      listingTitle = bid.title || 'Bid';
    }

    const slug = createSlug(listingTitle);
    const path = createResourceLocatorString(
      'ListingPage',
      routeConfiguration,
      { id: listingId, slug: slug },
      {}
    );

    // Use SPA navigation to avoid a full page reload and keep transition smooth.
    history.push(path);
  };

  const handleCardClick = e => {
    // Only navigate if not clicking on the button
    if (e?.target?.closest('button')) {
      return;
    }
    navigateToListingPage();
  };

  const handleMakeOffer = e => {
    e.stopPropagation();
    e.preventDefault();
    navigateToListingPage();
  };

  const rawDate =
    bid.date ||
    bid.eventDate ||
    bid.event_date ||
    bid.eventDateTime ||
    bid.listing?.attributes?.publicData?.eventDateTime ||
    bid.listing?.attributes?.publicData?.event_date ||
    bid.listing?.attributes?.publicData?.eventDate ||
    bid.listing?.attributes?.publicData?.date ||
    bid.createdAt ||
    bid.created_at ||
    bid.listing?.attributes?.publicData?.eventDateTime ||
    bid.listing?.attributes?.publicData?.event_date ||
    bid.listing?.attributes?.publicData?.eventDate ||
    '';

  const rawTime =
    bid.time ||
    bid.eventTime ||
    bid.event_time ||
    bid.listing?.attributes?.publicData?.event_time ||
    bid.listing?.attributes?.publicData?.eventTime ||
    '';
  const displayDate = formatDate(rawDate, rawTime);
  const displayLocation = formatCityStateLocation(bid.location || bid.venue);

  return (
    <div
      className={classNames(css.bidCard, { [css.dark]: theme === 'dark' })}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
      role="button"
      tabIndex={0}
      onKeyPress={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigateToListingPage();
        }
      }}
    >
      <div className={css.bidImageContainer}>
        <img src={bid.image} alt={bid.title} className={css.bidImage} />
      </div>
      <div className={css.bidContent}>
        <div className={css.priceLine}>
          {bid.currentBid > 0 && bid.maxBid > 0 ? (
            <span className={css.priceValue}>
              ${formatWholeDollar(bid.currentBid)} - ${formatWholeDollar(bid.maxBid)}
            </span>
          ) : bid.maxBid > 0 ? (
            <span className={css.priceValue}>Up to ${formatWholeDollar(bid.maxBid)}</span>
          ) : bid.currentBid > 0 ? (
            <span className={css.priceValue}>${formatWholeDollar(bid.currentBid)}</span>
          ) : (
            <span className={css.priceValue}>Price negotiable</span>
          )}
        </div>
        <div className={css.titleLine}>
          <span className={css.cardTitle}>{bid.title}</span>
        </div>
        {displayDate ? (
          <div className={css.dateLine}>
            <SvgIcon src={calendarIcon} alt="Calendar" className={css.iconImg} />
            <span className={css.dateTime}>{displayDate}</span>
          </div>
        ) : null}
        <div className={css.venueLine}>
          <SvgIcon src={locationIcon} alt="Location" className={css.iconImg} />
          <span className={css.venueLocation}>{displayLocation}</span>
        </div>
        {bid.buyersMessage && (
          <div className={css.descriptionLine}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Message</div>
            <span className={css.bidDescription}>{bid.buyersMessage}</span>
          </div>
        )}
        <button
          className={classNames(css.makeOfferButton, makeOfferButtonClassName)}
          onClick={handleMakeOffer}
        >
          Make an offer
        </button>
      </div>
    </div>
  );
};

BidCard.propTypes = {
  bid: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string.isRequired,
    date: PropTypes.string,
    eventDate: PropTypes.string,
    event_date: PropTypes.string,
    eventDateTime: PropTypes.string,
    time: PropTypes.string,
    eventTime: PropTypes.string,
    event_time: PropTypes.string,
    venue: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    numberOfTickets: PropTypes.number,
    currentBid: PropTypes.number,
    image: PropTypes.string,
    description: PropTypes.string,
    maxBid: PropTypes.number,
    status: PropTypes.string,
    eventType: PropTypes.string,
    listing: PropTypes.object, // Original Sharetribe listing object
    buyersMessage: PropTypes.string, // Buyer's message from publicData.message
    createdAt: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
  onMakeOffer: PropTypes.func, // Optional now - kept for backward compatibility
  theme: PropTypes.oneOf(['light', 'dark']),
  makeOfferButtonClassName: PropTypes.string,
};

export default BidCard;
const formatWholeDollar = amount => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return null;
  return Math.round(num).toLocaleString();
};
